import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { login, respondToChallenge } from '../partnerAuth';
import { COLOR, FONT, glass } from '../theme';

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challengeSession, setChallengeSession] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError('Enter your email and password'); return; }
    setBusy(true);
    setError('');
    try {
      const result = await login(email, password);
      if (result.challenge) setChallengeSession(result.session);
      else navigate('/partner');
    } catch (err) {
      setError(err.message || 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setBusy(true);
    setError('');
    try {
      await respondToChallenge(email, challengeSession, newPassword);
      navigate('/partner');
    } catch (err) {
      setError(err.message || 'Could not set your password');
    } finally {
      setBusy(false);
    }
  };

  const firstLogin = Boolean(challengeSession);

  return (
    <div style={S.page}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 180, damping: 20 }}
        style={S.card}
      >
        <div style={S.brand}>
          <span style={S.brandName}>Red<span style={S.brandSpeed}>Speed</span> GapGo</span>
        </div>

        <p style={S.kicker}>RESTAURANT PARTNER</p>
        <h1 style={S.title}>{firstLogin ? 'Set your password' : 'Sign in'}</h1>
        <p style={S.sub}>
          {firstLogin
            ? 'Choose a password you’ll use from now on.'
            : 'Access your orders and earnings.'}
        </p>

        {firstLogin ? (
          <form onSubmit={handleSetPassword}>
            <label style={S.label}>NEW PASSWORD</label>
            <input
              style={S.input}
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              placeholder="At least 8 characters"
              autoFocus
            />
            <label style={S.label}>CONFIRM PASSWORD</label>
            <input
              style={S.input}
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="Re-enter your password"
            />
            {error && <p style={S.error}>{error}</p>}
            <button type="submit" style={{ ...S.submit, opacity: busy ? 0.6 : 1 }} disabled={busy}>
              {busy ? 'Saving…' : 'Save and continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <label style={S.label}>EMAIL</label>
            <input
              style={S.input}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="you@restaurant.com.au"
              autoFocus
            />
            <label style={S.label}>PASSWORD</label>
            <input
              style={S.input}
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Your password"
            />
            {error && <p style={S.error}>{error}</p>}
            <button type="submit" style={{ ...S.submit, opacity: busy ? 0.6 : 1 }} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        <p style={S.help}>
          Need access? Contact RedSpeed at{' '}
          <a href="mailto:hello@redspeed.com.au" style={S.helpLink}>hello@redspeed.com.au</a>
        </p>
      </motion.div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', background: COLOR.bg, color: COLOR.text, fontFamily: FONT,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
  },
  card: { ...glass({ borderRadius: '24px', padding: '36px 30px' }), width: '100%', maxWidth: '400px' },
  brand: { display: 'flex', alignItems: 'center', marginBottom: '28px' },
  brandName: { fontSize: '19px', fontWeight: '700', letterSpacing: '-0.4px', color: COLOR.text },
  // Matches redspeed.com.au's own lockup: "Red" in text colour, "Speed" in brand red.
  brandSpeed: { color: COLOR.accent },
  kicker: { fontSize: '10px', letterSpacing: '2.5px', color: COLOR.accent, fontWeight: '700', margin: '0 0 8px' },
  title: { fontSize: '28px', fontWeight: '800', letterSpacing: '-0.8px', margin: '0 0 6px' },
  sub: { fontSize: '14px', color: COLOR.textMuted, margin: '0 0 26px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: '10px', letterSpacing: '2px', color: COLOR.textFaint, fontWeight: '600', marginBottom: '7px' },
  input: {
    width: '100%', boxSizing: 'border-box', marginBottom: '16px',
    background: 'rgba(255,255,255,0.04)', border: `1px solid ${COLOR.glassBorder}`,
    borderRadius: '13px', padding: '14px 16px', color: COLOR.text, fontSize: '16px',
    fontFamily: 'inherit', outline: 'none',
  },
  error: { fontSize: '13px', color: '#ff6b6b', margin: '0 0 14px' },
  submit: {
    width: '100%', background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`,
    color: '#fff', border: 'none', borderRadius: '14px', padding: '16px',
    fontSize: '16px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', marginTop: '4px',
  },
  help: { fontSize: '12.5px', color: COLOR.textFaint, textAlign: 'center', marginTop: '22px', lineHeight: 1.5 },
  helpLink: { color: COLOR.textMuted, fontWeight: '600' },
};
